// Facilities Service - API implementation for East End Prep Athletics
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Facility types and categories
export const FACILITY_TYPES = {
  GYMNASIUM: 'gymnasium',
  FIELD: 'field',
  TRAINING: 'training',
  LOCKER_ROOM: 'locker-room',
  STORAGE: 'storage',
  OTHER: 'other'
};

export const FACILITY_CATEGORIES = {
  INDOOR: 'indoor',
  OUTDOOR: 'outdoor',
  SUPPORT: 'support'
};

export const EQUIPMENT_CATEGORIES = {
  SPORTS: 'sports',
  TRAINING: 'training',
  SAFETY: 'safety',
  MAINTENANCE: 'maintenance',
  TECHNOLOGY: 'technology'
};

export const EQUIPMENT_TYPES = {
  SPORTS: 'sports',
  TRAINING: 'training',
  SAFETY: 'safety',
  MAINTENANCE: 'maintenance',
  TECHNOLOGY: 'technology'
};

export const BOOKING_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed'
};

export const MAINTENANCE_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

// Mock facilities data
const mockFacilities = [
  {
    id: 'gym-main',
    name: 'Main Gymnasium',
    type: FACILITY_TYPES.GYMNASIUM,
    category: FACILITY_CATEGORIES.INDOOR,
    description: 'Primary basketball and volleyball court with spectator seating',
    capacity: 800,
    dimensions: '94\'x50\'',
    surface: 'Hardwood maple flooring',
    features: [
      'Regulation basketball court',
      'Volleyball court setup',
      'Retractable bleachers (400 seats each side)',
      'Professional lighting system',
      'Sound system with wireless microphones',
      'Scoreboard with shot clock',
      'Air conditioning and heating'
    ],
    amenities: [
      'Concession stand',
      'Restrooms (4 total)',
      'First aid station',
      'Storage rooms (2)',
      'Coach offices (2)'
    ],
    images: [
      '/images/facilities/gym-main-1.jpg',
      '/images/facilities/gym-main-2.jpg',
      '/images/facilities/gym-main-3.jpg'
    ],
    availability: {
      monday: [{ start: '06:00', end: '22:00' }],
      tuesday: [{ start: '06:00', end: '22:00' }],
      wednesday: [{ start: '06:00', end: '22:00' }],
      thursday: [{ start: '06:00', end: '22:00' }],
      friday: [{ start: '06:00', end: '22:00' }],
      saturday: [{ start: '08:00', end: '20:00' }],
      sunday: [{ start: '10:00', end: '18:00' }]
    },
    maintenanceSchedule: [
      { date: '2024-02-15', type: 'Floor refinishing', status: 'scheduled' },
      { date: '2024-01-20', type: 'HVAC maintenance', status: 'completed' }
    ],
    contact: {
      manager: 'Mike Johnson',
      phone: '(615) 555-0123',
      email: 'mjohnson@eastendprep.edu'
    }
  },
  {
    id: 'field-football',
    name: 'Eagles Football Field',
    type: FACILITY_TYPES.FIELD,
    category: FACILITY_CATEGORIES.OUTDOOR,
    description: 'Regulation football field with track and field facilities',
    capacity: 1200,
    dimensions: '120 yards x 53.3 yards',
    surface: 'Natural grass with drainage system',
    features: [
      'Regulation football field',
      '8-lane 400m track',
      'Field event areas (shot put, discus, long jump, high jump)',
      'Stadium lighting for night games',
      'Press box with announcer booth',
      'Electronic scoreboard',
      'Irrigation system'
    ],
    amenities: [
      'Home team bleachers (800 seats)',
      'Visitor bleachers (400 seats)',
      'Concession stand',
      'Restrooms (6 total)',
      'Team benches with covers',
      'Equipment storage building'
    ],
    images: [
      '/images/facilities/football-field-1.jpg',
      '/images/facilities/football-field-2.jpg',
      '/images/facilities/football-field-3.jpg'
    ],
    availability: {
      monday: [{ start: '15:00', end: '19:00' }],
      tuesday: [{ start: '15:00', end: '19:00' }],
      wednesday: [{ start: '15:00', end: '19:00' }],
      thursday: [{ start: '15:00', end: '19:00' }],
      friday: [{ start: '15:00', end: '22:00' }],
      saturday: [{ start: '08:00', end: '22:00' }],
      sunday: [{ start: '12:00', end: '18:00' }]
    },
    maintenanceSchedule: [
      { date: '2024-03-01', type: 'Field aeration', status: 'scheduled' },
      { date: '2024-02-10', type: 'Track resurfacing', status: 'in-progress' }
    ],
    contact: {
      manager: 'Sarah Williams',
      phone: '(615) 555-0124',
      email: 'swilliams@eastendprep.edu'
    }
  },
  {
    id: 'field-baseball',
    name: 'Eagles Baseball Diamond',
    type: FACILITY_TYPES.FIELD,
    category: FACILITY_CATEGORIES.OUTDOOR,
    description: 'Regulation baseball field with dugouts and batting cages',
    capacity: 300,
    dimensions: '90\'x90\' diamond, 330\' foul lines',
    surface: 'Clay infield, grass outfield',
    features: [
      'Regulation baseball diamond',
      'Clay pitcher\'s mound',
      'Home plate and bases',
      'Outfield fence (6 feet high)',
      'Dugouts for home and visiting teams',
      'Batting cages (3)',
      'Bullpen areas'
    ],
    amenities: [
      'Bleacher seating (300 capacity)',
      'Scoreboard',
      'Equipment storage',
      'Water fountains',
      'Shade structures'
    ],
    images: [
      '/images/facilities/baseball-field-1.jpg',
      '/images/facilities/baseball-field-2.jpg'
    ],
    availability: {
      monday: [{ start: '15:00', end: '19:00' }],
      tuesday: [{ start: '15:00', end: '19:00' }],
      wednesday: [{ start: '15:00', end: '19:00' }],
      thursday: [{ start: '15:00', end: '19:00' }],
      friday: [{ start: '15:00', end: '19:00' }],
      saturday: [{ start: '08:00', end: '18:00' }],
      sunday: [{ start: '12:00', end: '17:00' }]
    },
    maintenanceSchedule: [
      { date: '2024-02-25', type: 'Infield grooming', status: 'scheduled' }
    ],
    contact: {
      manager: 'Tom Rodriguez',
      phone: '(615) 555-0125',
      email: 'trodriguez@eastendprep.edu'
    }
  },
  {
    id: 'training-weight-room',
    name: 'Eagles Weight Room',
    type: FACILITY_TYPES.TRAINING,
    category: FACILITY_CATEGORIES.INDOOR,
    description: 'Fully equipped weight training and conditioning facility',
    capacity: 40,
    dimensions: '40\'x60\'',
    surface: 'Rubber flooring with lifting platforms',
    features: [
      'Free weights (dumbbells 5-100 lbs)',
      'Olympic barbells and plates',
      'Power racks (4)',
      'Bench press stations (3)',
      'Cable machines (6)',
      'Cardio equipment (treadmills, bikes, ellipticals)',
      'Functional training area',
      'Stretching mats'
    ],
    amenities: [
      'Air conditioning',
      'Sound system',
      'Water fountains',
      'Towel service',
      'Equipment sanitizing stations'
    ],
    images: [
      '/images/facilities/weight-room-1.jpg',
      '/images/facilities/weight-room-2.jpg'
    ],
    availability: {
      monday: [{ start: '06:00', end: '20:00' }],
      tuesday: [{ start: '06:00', end: '20:00' }],
      wednesday: [{ start: '06:00', end: '20:00' }],
      thursday: [{ start: '06:00', end: '20:00' }],
      friday: [{ start: '06:00', end: '18:00' }],
      saturday: [{ start: '08:00', end: '16:00' }],
      sunday: [{ start: '10:00', end: '16:00' }]
    },
    maintenanceSchedule: [
      { date: '2024-02-20', type: 'Equipment inspection', status: 'scheduled' }
    ],
    contact: {
      manager: 'Coach Davis',
      phone: '(615) 555-0126',
      email: 'cdavis@eastendprep.edu'
    }
  },
  {
    id: 'locker-home',
    name: 'Home Team Locker Room',
    type: FACILITY_TYPES.LOCKER_ROOM,
    category: FACILITY_CATEGORIES.SUPPORT,
    description: 'Primary locker room facility for home teams',
    capacity: 50,
    dimensions: '30\'x40\'',
    surface: 'Tile flooring with drainage',
    features: [
      'Individual lockers (50)',
      'Bench seating',
      'Shower facilities (10 stalls)',
      'Coach office',
      'Equipment storage area',
      'Laundry facilities',
      'Training table'
    ],
    amenities: [
      'Heating and ventilation',
      'Hot water',
      'Towel hooks',
      'Bulletin boards',
      'First aid kit'
    ],
    images: [
      '/images/facilities/locker-room-1.jpg'
    ],
    availability: {
      monday: [{ start: '06:00', end: '22:00' }],
      tuesday: [{ start: '06:00', end: '22:00' }],
      wednesday: [{ start: '06:00', end: '22:00' }],
      thursday: [{ start: '06:00', end: '22:00' }],
      friday: [{ start: '06:00', end: '22:00' }],
      saturday: [{ start: '08:00', end: '20:00' }],
      sunday: [{ start: '10:00', end: '18:00' }]
    },
    maintenanceSchedule: [
      { date: '2024-02-18', type: 'Deep cleaning', status: 'scheduled' }
    ],
    contact: {
      manager: 'Facilities Team',
      phone: '(615) 555-0127',
      email: 'facilities@eastendprep.edu'
    }
  },
  {
    id: 'field-soccer',
    name: 'Eagles Soccer Field',
    type: FACILITY_TYPES.FIELD,
    category: FACILITY_CATEGORIES.OUTDOOR,
    description: 'Regulation soccer field with natural grass surface',
    capacity: 500,
    dimensions: '100-130 yards x 50-100 yards',
    surface: 'Natural grass with irrigation system',
    features: [
      'FIFA regulation soccer field',
      'Goal posts (regulation size)',
      'Corner flags and markers',
      'Sideline benches',
      'Field lighting for evening games',
      'Irrigation and drainage system',
      'Practice goals (2 additional)'
    ],
    amenities: [
      'Spectator bleachers (500 seats)',
      'Team benches with covers',
      'Equipment storage shed',
      'Water fountains',
      'Scoreboard',
      'Parking area'
    ],
    images: [
      '/images/facilities/soccer-field-1.jpg',
      '/images/facilities/soccer-field-2.jpg'
    ],
    availability: {
      monday: [{ start: '15:00', end: '19:00' }],
      tuesday: [{ start: '15:00', end: '19:00' }],
      wednesday: [{ start: '15:00', end: '19:00' }],
      thursday: [{ start: '15:00', end: '19:00' }],
      friday: [{ start: '15:00', end: '19:00' }],
      saturday: [{ start: '08:00', end: '18:00' }],
      sunday: [{ start: '12:00', end: '17:00' }]
    },
    maintenanceSchedule: [
      { date: '2024-03-10', type: 'Field aeration and seeding', status: 'scheduled' },
      { date: '2024-02-05', type: 'Goal post inspection', status: 'completed' }
    ],
    contact: {
      manager: 'Coach Martinez',
      phone: '(615) 555-0128',
      email: 'cmartinez@eastendprep.edu'
    }
  }
];

// Mock equipment datainventory
const mockEquipment = [
  {
    id: 'eq-001',
    name: 'Basketballs',
    category: EQUIPMENT_CATEGORIES.SPORTS,
    quantity: 25,
    available: 20,
    condition: 'Good',
    location: 'Main Gymnasium Storage',
    lastInspection: '2024-01-15',
    nextInspection: '2024-04-15',
    notes: '5 balls currently in use for practice'
  },
  {
    id: 'eq-002',
    name: 'Football Helmets',
    category: EQUIPMENT_CATEGORIES.SPORTS,
    quantity: 60,
    available: 55,
    condition: 'Excellent',
    location: 'Football Equipment Room',
    lastInspection: '2024-01-10',
    nextInspection: '2024-07-10',
    notes: 'All helmets reconditioned for season'
  },
  {
    id: 'eq-003',
    name: 'Weight Plates (45 lbs)',
    category: EQUIPMENT_CATEGORIES.TRAINING,
    quantity: 20,
    available: 20,
    condition: 'Good',
    location: 'Weight Room',
    lastInspection: '2024-01-20',
    nextInspection: '2024-04-20',
    notes: 'Regular maintenance required'
  },
  {
    id: 'eq-004',
    name: 'First Aid Kits',
    category: EQUIPMENT_CATEGORIES.SAFETY,
    quantity: 8,
    available: 8,
    condition: 'Good',
    location: 'Various Facilities',
    lastInspection: '2024-01-25',
    nextInspection: '2024-02-25',
    notes: 'Monthly inspection and restocking required'
  },
  {
    id: 'eq-005',
    name: 'Lawn Mowers',
    category: EQUIPMENT_CATEGORIES.MAINTENANCE,
    quantity: 3,
    available: 2,
    condition: 'Fair',
    location: 'Maintenance Shed',
    lastInspection: '2024-01-12',
    nextInspection: '2024-03-12',
    notes: 'One mower in for repairs'
  }
];

// Mock booking/scheduling data
const mockBookings = [
  {
    id: 'book-001',
    facilityId: 'gym-main',
    title: 'Basketball Practice - Varsity',
    date: '2024-02-15',
    startTime: '15:30',
    endTime: '17:00',
    bookedBy: 'Coach Johnson',
    contact: 'cjohnson@eastendprep.edu',
    status: 'confirmed',
    notes: 'Regular practice session'
  },
  {
    id: 'book-002',
    facilityId: 'field-football',
    title: 'Football Game vs. Central High',
    date: '2024-02-16',
    startTime: '19:00',
    endTime: '22:00',
    bookedBy: 'Athletic Director',
    contact: 'athletics@eastendprep.edu',
    status: 'confirmed',
    notes: 'Home game - setup required at 17:00'
  }
];

export class FacilitiesService {
  // Get authentication headers
  static getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  // Facility management methods
  static async getFacilities(filters = {}) {
    try {
      const response = await axios.get(`${API_BASE_URL}/facilities`, {
        params: filters
      });
      return response.data;
    } catch (error) {

      throw error;
    }
  }
  
  static async getFacilityById(id) {
    try {
      const response = await axios.get(`${API_BASE_URL}/facilities/${id}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }
  
  static async createFacility(facilityData) {
    try {
      const authHeaders = this.getAuthHeaders();
      const headers = {
        ...authHeaders
      };
      
      // Don't set Content-Type for FormData - let browser set it with boundary
      if (!(facilityData instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }
      
      const response = await axios.post(`${API_BASE_URL}/facilities`, facilityData, {
        headers
      });
      return response.data;
    } catch (error) {

      throw error;
    }
  }
  
  static async updateFacility(id, updates) {
    try {
      const authHeaders = this.getAuthHeaders();
      const headers = {
        ...authHeaders
      };
      
      // Don't set Content-Type for FormData - let browser set it with boundary
      if (!(updates instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }
      
      const response = await axios.put(`${API_BASE_URL}/facilities/${id}`, updates, {
        headers
      });
      return response.data;
    } catch (error) {

      throw error;
    }
  }
  
  static async deleteFacility(id) {
    try {
      const authHeaders = this.getAuthHeaders();
      await axios.delete(`${API_BASE_URL}/facilities/${id}`, {
        headers: authHeaders
      });
      return true;
    } catch (error) {

      throw error;
    }
  }
  
  // Equipment management methods
  static async getEquipment(filters = {}) {
    try {
      const response = await axios.get(`${API_BASE_URL}/equipment`, {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching equipment, falling back to mock data:', error);
      // Fallback to mock data until backend routes are implemented
      return mockEquipment.filter(item => {
        if (filters.category && item.category !== filters.category) return false;
        if (filters.location && !item.location.toLowerCase().includes(filters.location.toLowerCase())) return false;
        return true;
      });
    }
  }
  
  static async getEquipmentById(id) {
    try {
      const response = await axios.get(`${API_BASE_URL}/equipment/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching equipment:', error);
      throw error;
    }
  }
  
  static async createEquipment(equipmentData) {
    try {
      const response = await axios.post(`${API_BASE_URL}/equipment`, equipmentData);
      return response.data;
    } catch (error) {
      console.error('Error creating equipment, using mock response:', error);
      // Mock response until backend routes are implemented
      return { id: `eq-${Date.now()}`, ...equipmentData, created: true };
    }
  }
  
  static async updateEquipment(id, updates) {
    try {
      const response = await axios.put(`${API_BASE_URL}/equipment/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating equipment, using mock response:', error);
      // Mock response until backend routes are implemented
      return { id, ...updates, updated: true };
    }
  }
  
  static async checkoutEquipment(id, quantity, borrower) {
    try {
      const response = await axios.post(`${API_BASE_URL}/equipment/${id}/checkout`, {
        quantity,
        borrower
      });
      return response.data;
    } catch (error) {
      console.error('Error checking out equipment:', error);
      throw error;
    }
  }
  
  static async returnEquipment(id, quantity) {
    try {
      const response = await axios.post(`${API_BASE_URL}/equipment/${id}/return`, { quantity });
      return response.data;
    } catch (error) {
      console.error('Error returning equipment:', error);
      throw error;
    }
  }

  static async deleteEquipment(id) {
    try {
      const response = await axios.delete(`${API_BASE_URL}/equipment/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting equipment, using mock response:', error);
      // Mock response until backend routes are implemented
      return { id, deleted: true };
    }
  }

  // Booking/Scheduling methods
  static async getBookings(facilityId = null, date = null) {
    try {
      const params = {};
      if (facilityId) params.facilityId = facilityId;
      if (date) params.date = date;
      
      const response = await axios.get(`${API_BASE_URL}/bookings`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching bookings, falling back to mock data:', error);
      // Fallback to mock data until backend routes are implemented
      return mockBookings.filter(booking => {
        if (facilityId && booking.facilityId !== facilityId) return false;
        if (date && booking.date !== date) return false;
        return true;
      });
    }
  }
  
  static async createBooking(bookingData) {
    try {
      const response = await axios.post(`${API_BASE_URL}/bookings`, bookingData);
      return response.data;
    } catch (error) {
      console.error('Error creating booking, using mock response:', error);
      // Mock response until backend routes are implemented
      return { id: `book-${Date.now()}`, ...bookingData, status: 'pending', created: true };
    }
  }
  
  static async updateBooking(id, updates) {
    try {
      const response = await axios.put(`${API_BASE_URL}/bookings/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating booking, using mock response:', error);
      // Mock response until backend routes are implemented
      return { id, ...updates, updated: true };
    }
  }
  
  static async deleteBooking(id) {
    try {
      await axios.delete(`${API_BASE_URL}/bookings/${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting booking, using mock response:', error);
      // Mock response until backend routes are implemented
      return true;
    }
  }
  
  // Maintenance methods
  static async addMaintenanceRecord(facilityId, maintenanceData) {
    try {
      const response = await axios.post(`${API_BASE_URL}/facilities/${facilityId}/maintenance`, maintenanceData);
      return response.data;
    } catch (error) {
      console.error('Error adding maintenance record, using mock response:', error);
      // Mock response until backend routes are implemented
      return { id: `maint-${Date.now()}`, facilityId, ...maintenanceData, created: true };
    }
  }
  
  static async updateMaintenanceRecord(facilityId, recordId, updates) {
    try {
      const response = await axios.put(`${API_BASE_URL}/facilities/${facilityId}/maintenance/${recordId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating maintenance record, using mock response:', error);
      // Mock response until backend routes are implemented
      return { id: recordId, facilityId, ...updates, updated: true };
    }
  }
  
  // Maintenance requests methods
  static async getMaintenanceRequests() {
    try {
      const response = await axios.get(`${API_BASE_URL}/maintenance`);
      return response.data;
    } catch (error) {
      console.error('Error fetching maintenance requests, falling back to mock data:', error);
      // Fallback to mock data until backend routes are implemented
      return [];
    }
  }

  static async createMaintenanceRequest(requestData) {
    try {
      const response = await axios.post(`${API_BASE_URL}/maintenance`, requestData);
      return response.data;
    } catch (error) {
      console.error('Error creating maintenance request, using mock response:', error);
      // Mock response until backend routes are implemented
      return { id: `maint-req-${Date.now()}`, ...requestData, created: true };
    }
  }

  static async updateMaintenanceRequest(id, updates) {
    try {
      const response = await axios.put(`${API_BASE_URL}/maintenance/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating maintenance request, using mock response:', error);
      // Mock response until backend routes are implemented
      return { id, ...updates, updated: true };
    }
  }

  static async deleteMaintenanceRequest(id) {
    try {
      const response = await axios.delete(`${API_BASE_URL}/maintenance/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting maintenance request, using mock response:', error);
      // Mock response until backend routes are implemented
      return { id, deleted: true };
    }
  }

  // Statistics and reporting
  static async getFacilityStats() {
    try {
      const response = await axios.get(`${API_BASE_URL}/facilities/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching facility stats:', error);
      throw error;
    }
  }
  
  // Utility methods
  static formatTime(time) {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  }
  
  static async isTimeSlotAvailable(facilityId, date, startTime, endTime) {
    try {
      const response = await axios.get(`${API_BASE_URL}/bookings/availability`, {
        params: { facilityId, date, startTime, endTime }
      });
      return response.data.available;
    } catch (error) {
      console.error('Error checking availability:', error);
      return false;
    }
  }
}

export default FacilitiesService;